import {
  Bold,
  Italic,
  Quote,
  Code,
  Link,
  List,
  X,
  Image,
  ListOrdered,
  Minus
} from 'lucide-react';
import * as Yup from 'yup';
import { useCallback } from 'react';

// ─── Markdown preview renderer ──────────────────────────────────────────────

import { useState } from 'react';

export const renderMarkdown = (text) => {
  if (!text) return '';
  let html = text
    // Code blocks (must come before inline code)
    .replace(
      /```([\s\S]*?)```/g,
      '<pre class="bg-[#F4F4EE] border border-[#E8E8E0] rounded-lg p-4 my-4 overflow-x-auto"><code class="text-sm font-mono text-[#2A2A2A]">$1</code></pre>'
    )
    // Headings
    .replace(
      /^### (.+)$/gm,
      '<h3 class="text-xl font-semibold mt-6 mb-2 text-[#1A1A1A]">$1</h3>'
    )
    .replace(
      /^## (.+)$/gm,
      '<h2 class="text-2xl font-semibold mt-8 mb-3 text-[#1A1A1A]">$1</h2>'
    )
    .replace(
      /^# (.+)$/gm,
      '<h1 class="text-3xl font-semibold mt-8 mb-4 text-[#1A1A1A]">$1</h1>'
    )
    // Bold & italic
    .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    // Inline code
    .replace(
      /`(.+?)`/g,
      '<code class="bg-[#F4F4EE] px-1.5 py-0.5 rounded text-sm font-mono text-[#2A2A2A]">$1</code>'
    )
    // Blockquote
    .replace(
      /^> (.+)$/gm,
      '<blockquote class="border-l-4 border-[#D4A853] pl-5 italic text-[#666650] my-5">$1</blockquote>'
    )
    // Horizontal rule
    .replace(/^---$/gm, '<hr class="border-[#E8E8E0] my-8" />')
    // Unordered list items
    .replace(/^- (.+)$/gm, '<li class="ml-6 list-disc text-[#2A2A2A] my-1">$1</li>')
    // Ordered list items
    .replace(
      /^\d+\. (.+)$/gm,
      '<li class="ml-6 list-decimal text-[#2A2A2A] my-1">$1</li>'
    )
    // Links & images
    .replace(
      /!\[(.+?)\]\((.+?)\)/g,
      '<img src="$2" alt="$1" class="my-4 max-w-full rounded-lg" />'
    )
    .replace(
      /\[(.+?)\]\((.+?)\)/g,
      '<a href="$2" class="text-[#B8860B] underline underline-offset-2 hover:text-[#D4A853]">$1</a>'
    )
    // Paragraphs
    .replace(/\n\n/g, '</p><p class="mb-5 text-[#2A2A2A] leading-relaxed">')
    .replace(/\n/g, '<br />');
  return `<p class="mb-5 text-[#2A2A2A] leading-relaxed">${html}</p>`;
};

// ─── Validation schema ───────────────────────────────────────────────────────

export const validationSchema = Yup.object({
  title: Yup.string()
    .required('Title is required')
    .max(200, 'Title cannot exceed 200 characters'),
  content: Yup.string().required('Content is required'),
  excerpt: Yup.string().max(500, 'Excerpt cannot exceed 500 characters'),
  author: Yup.string(),
  categories: Yup.array().of(Yup.string()),
  tags: Yup.array().of(Yup.string()),
  status: Yup.string().oneOf(['draft', 'published', 'archived']),
  publish_date: Yup.string().nullable(),
  seo_title: Yup.string().max(60, 'SEO title cannot exceed 60 characters'),
  seo_description: Yup.string().max(
    160,
    'SEO description cannot exceed 160 characters'
  )
});

export const DEFAULT_VALUES = {
  title: '',
  content: '',
  excerpt: '',
  featured_image: '',
  author: '',
  categories: [],
  tags: [],
  status: 'draft',
  publish_date: '',
  seo_title: '',
  seo_description: ''
};

// ─── Sub-components ──────────────────────────────────────────────────────────

export function SidebarSection({ label, icon: Icon, children }) {
  return (
    <div>
      <label className="flex items-center gap-2 text-black text-[10px] font-semibold uppercase tracking-widest mb-2">
        <Icon size={11} className="shrink-0" />
        {label}
      </label>
      {children}
    </div>
  );
}

export function CharBar({ current, max }) {
  const pct = Math.min(current / max, 1);
  const over = current > max;
  const warn = !over && pct > 0.8;
  return (
    <div className="flex gap-2 justify-between items-center mt-1.5">
      <div className="overflow-hidden flex-1 h-px rounded-full bg-white/5">
        <div
          className={`h-full rounded-full transition-all duration-300 ${
            over ? 'bg-red-400' : warn ? 'bg-amber-400' : 'bg-white/15'
          }`}
          style={{ width: `${pct * 100}%` }}
        />
      </div>
      <span
        className={`text-[10px] tabular-nums shrink-0 ${
          over ? 'text-red-400' : warn ? 'text-amber-400' : 'text-white/20'
        }`}
      >
        {current}/{max}
      </span>
    </div>
  );
}

export function TagInput({ values, onChange, placeholder }) {
  const [inputVal, setInputVal] = useState('');

  const addTag = useCallback(() => {
    const trimmed = inputVal.trim().replace(/,$/, '');
    if (trimmed && !values.includes(trimmed)) {
      onChange([...values, trimmed]);
    }
    setInputVal('');
  }, [inputVal, values, onChange]);

  return (
    <div className="flex flex-wrap gap-1.5 p-2  border border-black/10 rounded-lg min-h-[38px] focus-within:border-[#003232]/40 transition-colors">
      {values.map((tag, i) => (
        <span
          key={i}
          className="flex items-center gap-1 px-2 py-0.5 bg-[#003232]/15 text-[#003232] text-[11px] rounded-full border border-[#003232]/20"
        >
          {tag}
          <button
            type="button"
            onClick={() => onChange(values.filter((_, idx) => idx !== i))}
            className="transition-colors hover:text-amber-100"
          >
            <X size={9} />
          </button>
        </span>
      ))}
      <input
        value={inputVal}
        onChange={(e) => setInputVal(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            addTag();
          }
          if (e.key === 'Backspace' && !inputVal && values.length) {
            onChange(values.slice(0, -1));
          }
        }}
        onBlur={addTag}
        placeholder={values.length === 0 ? placeholder : ''}
        className="flex-1 min-w-[80px] bg-transparent text-black/60 text-xs outline-none placeholder-black/20"
      />
    </div>
  );
}

// ─── Toolbar button configs ───────────────────────────────────────────────────

export const buildToolbar = (insertMarkdown, insertLinePrefix, insertBlock) => [
  {
    icon: Bold,
    label: 'Bold (Ctrl+B)',
    action: () => insertMarkdown('**', '**', 'bold text')
  },
  {
    icon: Italic,
    label: 'Italic (Ctrl+I)',
    action: () => insertMarkdown('*', '*', 'italic text')
  },
  { type: 'sep' },
  {
    label: 'H1',
    text: true,
    title: 'Heading 1',
    action: () => insertLinePrefix('# ')
  },
  {
    label: 'H2',
    text: true,
    title: 'Heading 2',
    action: () => insertLinePrefix('## ')
  },
  {
    label: 'H3',
    text: true,
    title: 'Heading 3',
    action: () => insertLinePrefix('### ')
  },
  { type: 'sep' },
  {
    icon: Quote,
    label: 'Blockquote',
    action: () => insertLinePrefix('> ')
  },
  {
    icon: Code,
    label: 'Inline code',
    action: () => insertMarkdown('`', '`', 'code')
  },
  {
    icon: List,
    label: 'Bullet list',
    action: () => insertLinePrefix('- ')
  },
  {
    icon: ListOrdered,
    label: 'Numbered list',
    action: () => insertLinePrefix('1. ')
  },
  { type: 'sep' },
  {
    icon: Link,
    label: 'Link',
    action: () => insertMarkdown('[', '](url)', 'link text')
  },
  {
    icon: Image,
    label: 'Image',
    action: () => insertMarkdown('![', '](url)', 'alt text')
  },
  {
    icon: Minus,
    label: 'Horizontal rule',
    action: () => insertBlock('\n---\n')
  }
];
